// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { formatUsDate } from '../../api/format'
import { useNavigate } from 'react-router-dom'
import { ClipboardPlus } from 'lucide-react'
import * as api from '../../api/client'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: a funnel on every column. Keys are the header labels, so the header map below finds
// its funnel by the label it prints. Each value is what the cell shows; the date stays raw.
const PLAN_FUNNELS: FunnelColumn<api.CareRxAssessment>[] = [
  { key: 'Drug / therapy', label: 'Drug / therapy', value: a => a.drug_name },
  { key: 'Diagnosis', label: 'Diagnosis', value: a => a.diagnosis },
  { key: 'Step', label: 'Step', value: a => `${a.current_step}/${a.total_steps}` },
  { key: 'Status', label: 'Status', value: a => a.status.replace(/_/g, ' ') },
  { key: 'Signature', label: 'Signature', value: a => a.signature_status?.replace(/_/g, ' ') },
  { key: 'Updated', label: 'Updated', value: a => a.updated_at },
]

/**
 * CareRx section on the patient detail page — surfaces this patient's Plans of Care so the
 * clinical record is unified (not siloed in the CareRx workspace). Permission/scope are
 * enforced by the API; if the caller can't see CareRx, this renders nothing.
 */
export default function PatientCareRxStrip({ patientId, patientName: _patientName, mrn: _mrn }: { patientId: string; patientName?: string; mrn?: string }) {
  const navigate = useNavigate()
  const [items, setItems] = useState<api.CareRxAssessment[] | null>(null)
  // D-CRX-25: CareRx documents (POTs, discharge summaries) on the chart. Fail-quiet; top hook block.
  const [crxDocs, setCrxDocs] = useState<{ id: string; kind: string; created_at: string; blob_path: string | null; attested?: boolean; envelope_status?: string | null }[]>([])

  useEffect(() => {
    if (!patientId) return
    api.listCareRxDocuments(patientId)
      .then(r => setCrxDocs((r.items ?? []).filter((d: { kind: string }) => d.kind === 'pot' || d.kind === 'discharge_summary')))
      .catch(() => setCrxDocs([]))
    api.listCareRxAssessments({ patient_id: patientId })
      .then(d => setItems(d.items))
      .catch(() => setItems(null)) // no access / not available — stay quiet
  }, [patientId])

  // D-UI-2: hook stays above the early return; the rows are empty until the plans arrive.
  const fx = useTableFunnels(items, PLAN_FUNNELS)

  if (!items) return null

  const view = (id: string) => navigate(`/carerx?assessment=${id}`)

  return (
    <>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '28px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClipboardPlus size={18} style={{ color: 'var(--color-primary)' }} /> Plans of Care ({items.length})
        </span>
        {/* WS2-6 (D-CRX-19): creation moved to the enrollment door; this strip is a read-only library. */}
        <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--color-text-mute)' }}>generated from the PCW workflow</span>
      </h2>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-surface)', overflow: 'hidden' }}>
        {items.length === 0 ? (
          <div style={{ padding: 16, color: 'var(--color-text-mute)', fontSize: 14 }}>
            No Plans of Care for this patient — plans are generated from the PCW workflow (stage 5).
          </div>
        ) : (
          <>
          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Drug / therapy', 'Diagnosis', 'Step', 'Status', 'Signature', 'Updated', ''].map(h =>
              <th key={h} style={th}>{h} {fx.funnel(h)}</th>)}</tr></thead>
            <tbody>
              {fx.rows.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={td}>{a.drug_name || '—'}</td>
                  <td style={td}>{a.diagnosis || '—'}</td>
                  <td style={td}>{a.current_step}/{a.total_steps}</td>
                  <td style={td}>{a.status.replace(/_/g, ' ')}</td>
                  <td style={td}>{a.signature_status?.replace(/_/g, ' ') || '—'}</td>
                  <td style={td}>{formatUsDate(a.updated_at as string)}</td>
                  <td style={td}><button style={linkBtn} onClick={() => view(a.id)}>View →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      {crxDocs.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>CareRx documents ({crxDocs.length})</div>
          {crxDocs.map(d => (
            <div key={d.id} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', color: 'var(--color-text-soft)' }}>
              <span style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10, letterSpacing: .3,
                padding: '1px 7px', borderRadius: 999, border: '1px solid var(--color-border)' }}>
                {d.kind === 'pot' ? 'POT' : 'Discharge'}
              </span>
              {formatUsDate(d.created_at)}
              {d.attested && <span style={{ fontSize: 10, fontWeight: 700, color: '#047857' }}>ATTESTED</span>}
              {d.envelope_status && <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: d.envelope_status === 'signed' ? '#047857' : d.envelope_status === 'voided' ? '#b91c1c' : '#92400e' }}>{d.envelope_status}</span>}
              {d.blob_path ? (
                <button onClick={() => { void api.openCareRxDocument(d.id) }}
                  style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', padding: 0 }}>
                  View / Print PDF
                </button>
              ) : <span style={{ fontSize: 11 }}>· render pending</span>}
            </div>
          ))}
        </div>
      )}
      </div>
    </>
  )
}


const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '9px 12px' }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: 0 }
